/**
 * The roles addresses play (D-198), as Core works them out (AddressBook): a role is played by
 * the address it is ticked on; one nobody ticked takes the address of another role, by its chain
 * of stand-ins ("*": the first address). The form shows it at once — "correspondence: as the
 * headquarters" — before anything is saved.
 */
export const DEFAULT_ROLES = {
  registered: { single: true, fallback: ['headquarters', '*'] },
  headquarters: { single: true, fallback: ['registered', '*'] },
  correspondence: { single: true, fallback: ['headquarters', 'registered'] },
  invoice: { single: true, fallback: ['registered', 'headquarters'] },
  delivery: { single: false, fallback: ['headquarters', 'registered'] },
  branch: { single: false, fallback: [] },
  other: { single: false, fallback: [] },
}

/** The tenant's roles from the dictionary `address_role`, in its order: [{key, label, single, fallback}]. */
export function addressRoles(meta) {
  const entries = meta.dictionary('address_role')?.entries || []
  if (!entries.length) return Object.entries(DEFAULT_ROLES).map(([key, r]) => ({ key, label: key, ...r }))
  return entries.map((e) => ({ key: e.key, label: meta.localized(e.label), single: !!e.meta?.single, fallback: Array.isArray(e.meta?.fallback) ? e.meta.fallback : [] }))
}

/**
 * Each role's address among these (in their order): its index, and through which role it came
 * (null: its own; "*": the first address).
 *
 * @param {Array<{roles?: string[]}>} addresses
 * @param {Array<{key: string, fallback: string[]}>} roles
 * @returns {Record<string, {index: number, via: string|null}>}
 */
export function resolveRoles(addresses, roles) {
  const list = (addresses || []).filter((a) => a && !isEmptyAddress(a))
  if (!list.length) return {}
  const byKey = Object.fromEntries(roles.map((r) => [r.key, r]))
  const own = {}
  list.forEach((a) => (a.roles || []).forEach((r) => { if (own[r] === undefined) own[r] = a }))
  const find = (role, seen) => {
    if (own[role]) return { address: own[role], via: null }
    for (const next of byKey[role]?.fallback || []) {
      if (next === '*') return { address: list[0], via: '*' }
      if (seen.has(next) || !byKey[next]) continue
      const found = find(next, new Set([...seen, role]))
      if (found) return { address: found.address, via: found.via ?? next }
    }
    return null
  }
  const out = {}
  for (const r of roles) {
    const found = find(r.key, new Set())
    if (found) out[r.key] = { index: addresses.indexOf(found.address), via: found.via }
  }
  return out
}

/** An address with nothing in it (and pointing at nothing). */
export const isEmptyAddress = (a) => !a.linked_address_uuid && !['street', 'building', 'postal_code', 'city', 'region', 'country'].some((p) => a[p])

/** A single role ticked on one address leaves every other; one that is not just toggles. */
export function toggleRole(addresses, index, role, roles) {
  const single = roles.find((r) => r.key === role)?.single
  const has = (addresses[index].roles || []).includes(role)
  return addresses.map((a, i) => {
    const current = a.roles || []
    if (i === index) return { ...a, roles: has ? current.filter((r) => r !== role) : [...current, role] }
    return single && !has && current.includes(role) ? { ...a, roles: current.filter((r) => r !== role) } : a
  })
}

/** The API's addresses → the form's (what the API takes back). */
export const addressInputs = (addresses) => (addresses || []).map((a) => ({
  uuid: a.uuid, roles: [...(a.roles || [])], label: a.label ?? null, street: a.street ?? null, building: a.building ?? null, postal_code: a.postal_code ?? null,
  city: a.city ?? null, region: a.region ?? null, country: a.country ?? null, linked_address_uuid: a.linked?.address_uuid ?? null,
}))

/** Two addresses the same place: street, number, postal code and town. */
export const sameAddress = (a, b) => ['street', 'building', 'postal_code', 'city'].every((p) => String(a?.[p] || '').trim().toLowerCase() === String(b?.[p] || '').trim().toLowerCase())
